import React from "react";
import { Draggable } from "react-beautiful-dnd";
import { ActionDropdown } from "./ActionDropdown";
import { TriggerTypeDropdown } from "./TriggerTypeDropdown";
import { DurationInput } from "./DurationInput";
import { TriggerType } from "../types";
import {
  ActionControlsContainer,
  RemoveActionIcon,
  DragHandle,
} from "../styles";
import { ReactComponent as CloseIconSvg } from "../assets/close.svg";
import { ReactComponent as DragIconSvg } from "../assets/drag.svg";
import { TRIGGER_TYPES } from "Components/common/constants";

interface ActionItemProps {
  actionIndex: number;
  action: VdaAction;
  availableActions: VdaActionDefinition[];
  availableTriggerTypes: TriggerType[];
  onActionSelect: (
    action: VdaActionDefinition,
    stepIndex: number,
    actionIndex: number
  ) => void;
  onTriggerTypeSelect: (
    triggerType: TriggerType,
    stepIndex: number,
    actionIndex: number
  ) => void;
  onDurationChange: (
    duration: string,
    stepIndex: number,
    actionIndex: number
  ) => void;
  onRemoveAction: (stepIndex: number, actionIndex: number) => void;
  stepIndex: number;
  isStepEnabled: boolean;
}

export const ActionItem: React.FC<ActionItemProps> = ({
  actionIndex,
  action,
  availableActions,
  availableTriggerTypes,
  onActionSelect,
  onTriggerTypeSelect,
  onDurationChange,
  onRemoveAction,
  stepIndex,
  isStepEnabled,
}) => {
  const isCountdown =
    action.actionParameters &&
    action.actionParameters.length > 0 &&
    action.actionParameters[0].triggerType === TRIGGER_TYPES.DURATION;

  const isActionDropdownDisabled = !isStepEnabled;

  const isDragDisabled = false;

  return (
    <Draggable
      draggableId={`action-${stepIndex}-${actionIndex}`}
      index={actionIndex}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <ActionControlsContainer
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <DragHandle {...provided.dragHandleProps} disabled={isDragDisabled}>
            <DragIconSvg />
          </DragHandle>

          <ActionDropdown
            actions={availableActions}
            selectedAction={
              availableActions.find(
                (actionDef) => actionDef.actionType === action.actionType
              ) || null
            }
            onActionSelect={(selectedAction) =>
              onActionSelect(selectedAction, stepIndex, actionIndex)
            }
            disabled={isActionDropdownDisabled}
          />

          {/* Show trigger type dropdown for all actions */}
          <TriggerTypeDropdown
            triggerTypes={availableTriggerTypes}
            selectedTriggerType={
              action.actionParameters?.[0]?.triggerType
                ? availableTriggerTypes.find(
                    (type) =>
                      type.key === action.actionParameters[0].triggerType
                  ) || null
                : null
            }
            onTriggerTypeSelect={(selectedTriggerType) =>
              onTriggerTypeSelect(selectedTriggerType, stepIndex, actionIndex)
            }
            disabled={!isStepEnabled}
          />

          {isCountdown && (
            <DurationInput
              value={action.actionParameters?.[0]?.duration?.toString() || ""}
              onChange={(value) =>
                onDurationChange(value, stepIndex, actionIndex)
              }
              disabled={!isStepEnabled}
            />
          )}

          <RemoveActionIcon
            onClick={() => {
              if (isStepEnabled) {
                onRemoveAction(stepIndex, actionIndex);
              }
            }}
            style={
              !isStepEnabled ? { opacity: 0.5, cursor: "not-allowed" } : {}
            }
          >
            <CloseIconSvg />
          </RemoveActionIcon>
        </ActionControlsContainer>
      )}
    </Draggable>
  );
};
